import React from "react";
import styled from "styled-components";
import {
  colors,
  layout,
  Image,
  media,
  PageSection,
  PressLogos,
  SectionSlideshowClassName,
  Theme,
} from "@literati/public-ui-shared-react";
import TeacherPartnerSectionRewards from "components/TeacherPartner/TeacherPartnerSectionRewards";
import TeacherPartnerSectionAboutClubs, {
  ITEMS_DEFAULT,
} from "components/TeacherPartner/TeacherPartnerSectionAboutClubs";
import TeacherPartnerSectionReaderStats from "components/TeacherPartner/TeacherPartnerSectionReaderStats";
import TeacherPartnerSectionTestimonials, {
  sectionSlideshowDefaultProps,
} from "components/TeacherPartner/TeacherPartnerSectionTestimonials";
import TeacherPartnerSectionMissionStatement, {
  missionStatementPropsDefaults,
} from "components/TeacherPartner/TeacherPartnerSectionMissionStatement";
import { SchoolBoxAffiliationHero } from "components/SchoolBox/SchoolBoxAffiliationHero";
import { Affiliate } from "components/SchoolBox/schoolBoxTypes";
import { SubscriptionPlanCamelCase } from "lib/api/subscription";

/** This is the className that will be pre-pended to all of `SchoolBoxLanding`'s inner component classNames */
export const SCHOOL_BOX_EXPERIMENT_CLASS_BASE = "schoolbox-landing";

/** These are all of the internal classNames that are used within the SchoolBoxLanding component */
export const SchoolBoxLandingClassName = {
  BASE: SCHOOL_BOX_EXPERIMENT_CLASS_BASE,
} as const;

const Container = styled.div`
  .${SectionSlideshowClassName.BASE} {
    ${layout.sectionPadding}
    padding-top: 0;

    ${({ theme }) => theme.media.lg} {
      padding-top: 0;
    }
  }
`;

const StyledTeacherPartnerSectionAboutClubs = styled(
  TeacherPartnerSectionAboutClubs
)`
  padding-top: 0;
`;

const StyledImage = styled(Image)`
  background-color: ${colors.pulpWhite};
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const StyledTeacherPartnerSectionMissionStatement = styled(
  TeacherPartnerSectionMissionStatement
)`
  ${media["max:sm"]} {
    padding-top: 2rem;
  }
`;

export interface SchoolBoxLandingProps {
  affiliate?: Affiliate | null;
  className?: string;
  plans?: SubscriptionPlanCamelCase[];
}

export const SchoolBoxLanding = ({
  affiliate,
  className = "",
}: SchoolBoxLandingProps) => {
  const ctaLink = "#search";

  return (
    <Theme>
      <Container
        className={`${SchoolBoxLandingClassName.BASE} ${className}`}
        id="search"
      >
        <SchoolBoxAffiliationHero
          affiliate={affiliate}
          subtext="Support your school and get personalized book boxes delivered to your door. For a limited time, your school will earn $100 in bonus sales towards your book fair rewards eligible revenue."
        />
        <TeacherPartnerSectionRewards
          bgColor="pulpWhite"
          ctaLink={ctaLink}
          primaryHeading="Growing readers and school resources, book by book."
          subtext="With a choice of books picked just for them, your child can experience the joy in reading. Literati credits $100 in rewards back to your child’s school."
        />
        <StyledTeacherPartnerSectionAboutClubs
          bgColor="frenchBlueTint4"
          insetCardEyebrow="HOW OUR BOOK CLUBS WORK"
          insetCardHeading="5 Personalized books in every box. Delivered straight to your door."
          items={ITEMS_DEFAULT}
          gradient={{ topColor: "pulpWhite", bottomColor: "frenchBlueTint4" }}
        />
        <TeacherPartnerSectionReaderStats
          eyebrow="SUBSCRIBER STATS"
          bgColor="frenchBlueTint4"
        />
        <TeacherPartnerSectionTestimonials
          {...sectionSlideshowDefaultProps}
          pageSectionProps={{
            bgColor: "frenchBlueTint4",
            primaryHeading: "Real Readers. Real Progress.",
          }}
        />
        <PageSection bgColor="pulpWhite">
          <PressLogos />
        </PageSection>
        <StyledTeacherPartnerSectionMissionStatement
          bgColor="pulpWhite"
          ctaLabel="Join Literati Book Clubs"
          ctaLink={ctaLink}
          infoCTAProps={missionStatementPropsDefaults}
        />
        <StyledImage
          contain
          src={
            "https://images.ctfassets.net/vqtz4cq2w5ts/LoZ08V9w3G7BG8OhvYu3h/3bd7aa991344fec53f6accc3880ad767/Homepage_Kids_Focus_Overhead_Rainbow_Books.png"
          }
          width={1440}
          height={288}
          aria-hidden
          alt=""
        />
      </Container>
    </Theme>
  );
};

export default SchoolBoxLanding;
